import React from 'react';
import {
    AlertTriangle,
    TrendingUp,
    TrendingDown,
    DollarSign,
    Layers,
    PieChart,
    ArrowRight,
    Clock,
    Zap,
    Pause,
    Play,
    BarChart3
} from 'lucide-react';
import { formatCurrency } from '../../utils/helpers';

const InsightCard = ({ insight, onAction, onPause, onResume, onScale, compact = false }) => {
    const getPriorityConfig = (priority) => {
        switch (priority) {
            case 'critical':
                return {
                    borderColor: 'var(--color-error)',
                    bgGradient: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(239, 68, 68, 0.02))',
                    badge: { bg: 'var(--color-error)', text: 'white', label: 'Critical' }
                };
            case 'high':
                return {
                    borderColor: 'rgb(249, 115, 22)',
                    bgGradient: 'linear-gradient(135deg, rgba(249, 115, 22, 0.1), rgba(249, 115, 22, 0.02))',
                    badge: { bg: 'rgb(249, 115, 22)', text: 'white', label: 'High' }
                };
            case 'medium':
                return {
                    borderColor: 'rgb(234, 179, 8)',
                    bgGradient: 'linear-gradient(135deg, rgba(234, 179, 8, 0.1), rgba(234, 179, 8, 0.02))',
                    badge: { bg: 'rgb(234, 179, 8)', text: 'black', label: 'Medium' }
                };
            default:
                return {
                    borderColor: 'var(--color-success)',
                    bgGradient: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1), rgba(34, 197, 94, 0.02))',
                    badge: { bg: 'var(--color-success)', text: 'white', label: 'Low' }
                };
        }
    };

    const getTypeIcon = (type) => {
        switch (type) {
            case 'budget_waste':
                return AlertTriangle;
            case 'scale_opportunity':
                return TrendingUp;
            case 'trend_alert':
                return TrendingDown;
            case 'creative_fatigue':
                return Layers;
            case 'placement_optimization':
                return PieChart;
            default:
                return Zap;
        }
    };

    const getUrgencyLabel = (urgency) => {
        switch (urgency) {
            case 'immediate':
                return 'Act now';
            case 'today':
                return 'Today';
            case 'this_week':
                return 'This week';
            default:
                return 'Monitor';
        }
    };

    const getPlatformIcon = (platform) => {
        switch (platform?.toLowerCase()) {
            case 'meta':
                return '📘';
            case 'google':
                return '🔵';
            case 'tiktok':
                return '🎵';
            case 'amazon':
                return '📦';
            default:
                return '📊';
        }
    };

    const config = getPriorityConfig(insight.priority);
    const TypeIcon = getTypeIcon(insight.type);

    // Compact mode for horizontal scrolling insight bar
    if (compact) {
        return (
            <div
                style={{
                    background: config.bgGradient,
                    border: '1px solid var(--color-border)',
                    borderLeft: `4px solid ${config.borderColor}`,
                    borderRadius: 'var(--radius-lg)',
                    padding: 'var(--space-3)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--space-2)',
                    transition: 'all var(--transition-fast)',
                    cursor: 'pointer',
                    height: '100%'
                }}
                onClick={onAction}
                onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = 'var(--shadow-md)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.transform = 'translateY(0)';
                }}
            >
                {/* Header */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <span style={{
                        background: config.badge.bg,
                        color: config.badge.text,
                        padding: '2px 6px',
                        borderRadius: 'var(--radius-full)',
                        fontSize: '10px',
                        fontWeight: 600
                    }}>
                        {config.badge.label}
                    </span>
                    <TypeIcon size={16} style={{ color: config.borderColor }} />
                </div>

                {/* Title */}
                <h4 style={{
                    margin: 0,
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 600,
                    color: 'var(--color-text-primary)',
                    lineHeight: 1.3
                }}>
                    {insight.title}
                </h4>

                {/* Brief Description */}
                <p style={{
                    margin: 0,
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-secondary)',
                    lineHeight: 1.4,
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                }}>
                    {insight.description}
                </p>

                {/* Impact badge if available */}
                {insight.impactAmount != null && insight.impactAmount > 0 && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '2px 6px',
                        background: insight.impactType === 'save'
                            ? 'rgba(34, 197, 94, 0.15)'
                            : 'rgba(124, 58, 237, 0.15)',
                        borderRadius: 'var(--radius-sm)',
                        width: 'fit-content',
                        fontSize: '10px',
                        fontWeight: 600,
                        color: insight.impactType === 'save'
                            ? 'var(--color-success)'
                            : 'var(--color-primary)'
                    }}>
                        <DollarSign size={10} />
                        {insight.impactType === 'save' ? 'Save' : 'Gain'} {formatCurrency(insight.impactAmount)}
                    </div>
                )}

                {/* Footer */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: 'auto',
                    fontSize: 'var(--font-size-xs)'
                }}>
                    <span style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-1)',
                        color: 'var(--color-text-tertiary)'
                    }}>
                        {getPlatformIcon(insight.entity?.platform || insight.platform)}
                        <span style={{
                            maxWidth: '100px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>
                            {insight.objective || 'Campaign'}
                        </span>
                    </span>
                    {insight.action && (
                        <span style={{
                            color: 'var(--color-primary)',
                            fontWeight: 500
                        }}>
                            {insight.action} →
                        </span>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div
            style={{
                background: config.bgGradient,
                border: '1px solid var(--color-border)',
                borderLeft: `4px solid ${config.borderColor}`,
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--space-4)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-3)',
                transition: 'all var(--transition-fast)',
                cursor: 'pointer'
            }}
            onClick={onAction}
            onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = 'var(--shadow-md)';
                e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'translateY(0)';
            }}
        >
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)'
                }}>
                    <span style={{
                        background: config.badge.bg,
                        color: config.badge.text,
                        padding: '2px 8px',
                        borderRadius: 'var(--radius-full)',
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 600
                    }}>
                        {config.badge.label}
                    </span>
                    <span style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-tertiary)'
                    }}>
                        <Clock size={12} />
                        {getUrgencyLabel(insight.urgency)}
                    </span>
                </div>
                <TypeIcon size={20} style={{ color: config.borderColor }} />
            </div>

            {/* Title */}
            <h3 style={{
                margin: 0,
                fontSize: 'var(--font-size-md)',
                fontWeight: 600,
                color: 'var(--color-text-primary)',
                lineHeight: 1.3
            }}>
                {insight.title}
            </h3>

            {/* Description */}
            <p style={{
                margin: 0,
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-secondary)',
                lineHeight: 1.5
            }}>
                {insight.description}
            </p>

            {/* Reasoning */}
            {insight.reasoning && (
                <div style={{
                    padding: 'var(--space-3)',
                    background: 'var(--color-surface)',
                    borderRadius: 'var(--radius-md)',
                    borderLeft: '3px solid var(--color-primary)'
                }}>
                    <p style={{
                        margin: 0,
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 600,
                        color: 'var(--color-text-primary)',
                        marginBottom: 'var(--space-1)'
                    }}>
                        Why this matters:
                    </p>
                    <p style={{
                        margin: 0,
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-secondary)',
                        lineHeight: 1.5
                    }}>
                        {insight.reasoning}
                    </p>
                </div>
            )}

            {/* Expected Outcome */}
            {insight.expectedOutcome && (
                <div style={{
                    padding: 'var(--space-3)',
                    background: 'rgba(34, 197, 94, 0.05)',
                    borderRadius: 'var(--radius-md)',
                    borderLeft: '3px solid var(--color-success)'
                }}>
                    <p style={{
                        margin: 0,
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 600,
                        color: 'var(--color-text-primary)',
                        marginBottom: 'var(--space-1)'
                    }}>
                        Expected outcome:
                    </p>
                    <p style={{
                        margin: 0,
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-secondary)',
                        lineHeight: 1.5
                    }}>
                        {insight.expectedOutcome}
                    </p>
                </div>
            )}

            {/* Impact - only show if impactAmount exists */}
            {insight.impactAmount != null && insight.impactAmount > 0 && (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    padding: 'var(--space-2) var(--space-3)',
                    background: insight.impactType === 'save'
                        ? 'rgba(34, 197, 94, 0.1)'
                        : 'rgba(124, 58, 237, 0.1)',
                    borderRadius: 'var(--radius-md)',
                    width: 'fit-content'
                }}>
                    <DollarSign size={14} style={{
                        color: insight.impactType === 'save'
                            ? 'var(--color-success)'
                            : 'var(--color-primary)'
                    }} />
                    <span style={{
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 600,
                        color: insight.impactType === 'save'
                            ? 'var(--color-success)'
                            : 'var(--color-primary)'
                    }}>
                        {insight.impactType === 'save' ? 'Save' : 'Gain'} {formatCurrency(insight.impactAmount)}
                    </span>
                </div>
            )}

            {/* Footer */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: 'auto',
                paddingTop: 'var(--space-2)',
                borderTop: '1px solid var(--color-border-light)'
            }}>
                {/* Entity Info */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-tertiary)'
                }}>
                    <span>{getPlatformIcon(insight.entity?.platform)}</span>
                    <span style={{
                        maxWidth: '150px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}>
                        {insight.entity?.name || 'Unknown'}
                    </span>
                </div>

                {/* Action Buttons */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-2)'
                }}>
                    {/* Quick Actions based on insight type */}
                    {insight.type === 'budget_waste' && onPause && (
                        <ActionButton
                            icon={Pause}
                            label="Pause"
                            color="var(--color-warning)"
                            onClick={(e) => {
                                e.stopPropagation();
                                onPause(insight);
                            }}
                        />
                    )}
                    {insight.type === 'scale_opportunity' && onScale && (
                        <ActionButton
                            icon={TrendingUp}
                            label="Scale"
                            color="var(--color-success)"
                            onClick={(e) => {
                                e.stopPropagation();
                                onScale(insight);
                            }}
                        />
                    )}
                    {insight.entity?.status === 'PAUSED' && onResume && (
                        <ActionButton
                            icon={Play}
                            label="Resume"
                            color="var(--color-success)"
                            onClick={(e) => {
                                e.stopPropagation();
                                onResume(insight);
                            }}
                        />
                    )}

                    {/* View Details */}
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onAction();
                        }}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-1)',
                            padding: 'var(--space-1) var(--space-2)',
                            background: 'transparent',
                            border: 'none',
                            color: 'var(--color-primary)',
                            fontSize: 'var(--font-size-xs)',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all var(--transition-fast)'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'var(--color-surface-hover)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                        }}
                    >
                        {insight.actionLabel || 'View Details'}
                        <ArrowRight size={14} />
                    </button>
                </div>
            </div>
        </div>
    );
};

// Quick Action Button Component
const ActionButton = ({ icon: Icon, label, color, onClick }) => (
    <button
        onClick={onClick}
        title={label}
        style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            padding: '4px 8px',
            background: `${color}15`,
            border: `1px solid ${color}40`,
            borderRadius: 'var(--radius-md)',
            color: color,
            fontSize: 'var(--font-size-xs)',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all var(--transition-fast)'
        }}
        onMouseEnter={(e) => {
            e.currentTarget.style.background = `${color}25`;
        }}
        onMouseLeave={(e) => {
            e.currentTarget.style.background = `${color}15`;
        }}
    >
        <Icon size={12} />
        {label}
    </button>
);

export default InsightCard;
